//Library Imports
import { Col, Row } from 'antd';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { FC, useState, useEffect } from 'react';
import store, { RootState } from '../../../store/index';
import { useHistory, useLocation } from 'react-router-dom';

//Local Imports
import { resetAllData } from '../utils';
import AddCampaign from '../Shared/AddCampaign';
import CampaignList from '../Shared/CampaignList';
import CampaignCard from '../Shared/CampaignCard';
import { ICampaignItem } from 'modules/Campaigns/interfaces';
import EmptyTabInfo from 'modules/Content/Shared/EmptyTabInfo';
import InfiniteLoader from 'modules/Common/Components/InfiniteLoader';
import { CAMPAIGNS_LIST, DEFAULT_CAMPAIGN_IMAGE } from 'constants/types';
import ConfirmDialogWithModal from 'modules/Common/Components/ConfirmDialogWithModal';
import CreateCampaign from 'modules/Campaigns/CampaignAssets/Components/CreateCampaign';
import { deleteCampaign, getCampaignList, resetCampaignFilters } from '../CampaignAssets/actions';
import AssetsListPlaceHolder from 'modules/Campaigns/AssetsWithOutCampaigns/Components/AssetsListPlaceHolder';
import AssetsCardPlaceHolderResponsive from '../AssetsWithOutCampaigns/Components/AssetsCardPlaceHolderResponsive';

interface IMyCampaign {
  isListView?: boolean;
  createCampaign?: () => void;
  userFilter?: { label: string; value: string };
  emailTypes?: { label: string; value: string };
  datesObj?: { start_date: string; end_date: string };
  searchFilter?: string;
  isFiltered?: boolean;
  dateFilter?: any;
}

const MyCampaigns: FC<IMyCampaign> = (props) => {
  // Local variables
  const history = useHistory();
  const DEFAULT_PAGE_LIMIT = 20;
  const location = useLocation();
  const {
    isListView,
    userFilter = { label: 'by User', value: 'default' },
    emailTypes = { label: 'All Types', value: 'all' },
    datesObj,
    searchFilter,
    isFiltered = false,
    dateFilter,
  } = props;

  // Redux States
  const reset: boolean = useSelector((state: RootState) => state.campaigns.reset);
  const loading: boolean = useSelector((state: RootState) => state.campaigns.loading);
  const totalCount: number = useSelector((state: RootState) => state.campaigns.totalCount);
  const campaignsSearchSuccess = useSelector((state: RootState) => state.campaigns.success);
  const campaigns: ICampaignItem[] = useSelector((state: RootState) => state.campaigns.data);
  const campaignsSearchErrorMessage = useSelector((state: RootState) => state.campaigns.message);
  const prevCampaignId: number = useSelector((state: RootState) => state.campaigns.prevCampaignId);

  // Local States
  const [showConfirm, setShowConfirm] = useState(false);
  const [indexState, setIndexState] = useState<number>(0);
  const [campaignIdState, setCampaignIdState] = useState<string>('');
  const [assetCountState, setAssetCountState] = useState<number>(0);
  const storedCampaignId = useSelector((state: RootState) => state.emails.campaignId.toString());

  const showDeleteConfirmation = () => {
    setShowConfirm(true);
  };

  const deleteHandler = (index: any, campaignId: string, assetCount: number): void => {
    setIndexState(index);
    setCampaignIdState(campaignId);
    setAssetCountState(assetCount);
    if (showConfirm && campaignId === campaignIdState) {
      deleteCampaign(indexState, campaignIdState, assetCountState);
      setShowConfirm(false);
    }
  };
  const onConfirmDialogCancel = () => {
    setShowConfirm(false);
  };

  const campaignClick = (campaignId: string) => {
    resetAllData(prevCampaignId, parseInt(campaignId));
    if (searchFilter !== '' || emailTypes.value !== 'all' || userFilter.value !== 'default' || dateFilter.value !== 'qtr') {
      store.dispatch({ type: CAMPAIGNS_LIST.RESET_FLAG });
    }
    const campaignDetailsUrl = `${location.pathname.endsWith('/') ? location.pathname : `${location.pathname}/`}content${
      location.search
    }?campaign_id=${campaignId}`;
    history.push(campaignDetailsUrl);
  };

  const getPagination = () => {
    let offset = campaigns.length;
    let _params: any = {};
    // checking selected filters
    if (emailTypes.value !== 'all') _params.email_type = emailTypes.value === 'adhoc' ? 1 : 0;
    if (userFilter.value !== 'default') _params.user_id = userFilter.value;
    if (searchFilter) _params.search_campaign = searchFilter;
    if (datesObj) {
      _params.start_date = datesObj.start_date;
      _params.end_date = datesObj.end_date;
    }
    return { offset: offset, limit: DEFAULT_PAGE_LIMIT, ..._params };
  };

  const loadFiltered = () => {
    if (!loading) {
      getCampaignList(getPagination(), false);
    }
  };

  const loadWithFilter = () => {
    if (!loading) {
      getCampaignList(getPagination(), isFiltered);
    }
  };

  useEffect(() => {
    if (emailTypes.value !== 'all' || userFilter.value !== 'default') {
      store.dispatch({ type: CAMPAIGNS_LIST.RESET_FLAG });
      loadWithFilter();
    }
  }, [emailTypes, userFilter, searchFilter, datesObj]);

  useEffect(() => {
    if (campaigns.length === 0 || reset) {
      getCampaignList(getPagination(), false);
    }
    window.scrollTo(0, 0);
    return () => {
      store.dispatch({ type: CAMPAIGNS_LIST.RESET_FLAG });
    };
  }, []);

  return (
    <Wrapper>
      {showConfirm ? (
        <DialogWrapper>
          <ConfirmDialogWithModal
            cancel="Cancel"
            confirm="Delete"
            onConfirm={() => deleteHandler(indexState, campaignIdState, assetCountState)}
            onCancel={onConfirmDialogCancel}
            title={'Are you sure you want to delete this campaign?'}
            titleQuestion={null}
          />
        </DialogWrapper>
      ) : null}
      <h3>{`All Campaigns (${totalCount})`}</h3>
      <InfiniteLoader
        dataLength={campaigns.length}
        next={() => loadFiltered()}
        hasMore={campaigns.length < totalCount}
        loader={isListView ? <AssetsListPlaceHolder loading={loading} /> : <AssetsCardPlaceHolderResponsive count={4} loading={loading} />}
      >
        {loading && [...campaigns].length === 0 ? (
          isListView ? (
            <AssetsListPlaceHolder loading={loading} />
          ) : (
            <AssetsCardPlaceHolderResponsive count={4} loading={loading} />
          )
        ) : !loading && [...campaigns].length === 0 ? (
          !loading && isFiltered ? (
            <EmptyTabInfo description={campaignsSearchSuccess ? 'No campaign(s) found.' : campaignsSearchErrorMessage} />
          ) : (
            !loading && [...campaigns].length === 0 && <EmptyTabInfo description={'You have no campaigns created.'} />
          )
        ) : isListView ? (
          <>
            {[...campaigns].map((campaign: ICampaignItem, index: number) => (
              <div key={index}>
                <CampaignList
                  deleteHandler={deleteHandler}
                  showDeleteConfirmation={showDeleteConfirmation}
                  index={index}
                  campaignId={campaign.campaign_id?.toString()}
                  imgUrlOne={campaign?.thumbnails[0] ? campaign.thumbnails[0] : DEFAULT_CAMPAIGN_IMAGE}
                  imgUrlTwo={campaign?.thumbnails[1] ? campaign.thumbnails[1] : DEFAULT_CAMPAIGN_IMAGE}
                  imgUrlThree={campaign?.thumbnails[2] ? campaign.thumbnails[2] : DEFAULT_CAMPAIGN_IMAGE}
                  imgUrlFour={campaign?.thumbnails[3] ? campaign.thumbnails[3] : DEFAULT_CAMPAIGN_IMAGE}
                  audience={campaign.total_contacts?.toString()}
                  send={campaign.total_sent?.toString()}
                  mail={campaign.total_delivered?.toString()}
                  views={campaign.total_open?.toString()}
                  clicks={campaign.total_clicks?.toString()}
                  heading={campaign.name}
                  published={campaign.last_update}
                  emailsCount={campaign.emails_count}
                  onCardClick={() => {
                    if (campaign?.campaign_id?.toString() !== storedCampaignId) {
                      resetCampaignFilters();
                    }
                    campaignClick(campaign.campaign_id?.toString());
                  }}
                  automation={campaign?.workflow === 1}
                />
              </div>
            ))}
            <div style={{ width: '5em' }} className="add-btn-dropdown d-flex align-content-center justify-content-center flex-wrap list-add">
              <CreateCampaign hideDynamic={true}>
                <AddCampaign className="add-campaign-plus-style-list" />
              </CreateCampaign>
            </div>
          </>
        ) : (
          <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 40, xl: 56, xxl: 56 }}>
            <>
              {[...campaigns].map((campaign, index) => (
                <Col key={index} className="gutter-row" xs={18} sm={12} md={8} lg={8} xl={8} xxl={6}>
                  <CampaignCard
                    deleteHandler={deleteHandler}
                    showDeleteConfirmation={showDeleteConfirmation}
                    index={index}
                    campaignId={campaign?.campaign_id?.toString()}
                    imgUrlOne={campaign?.thumbnails[0] ? campaign.thumbnails[0] : DEFAULT_CAMPAIGN_IMAGE}
                    imgUrlTwo={campaign?.thumbnails[1] ? campaign.thumbnails[1] : DEFAULT_CAMPAIGN_IMAGE}
                    imgUrlThree={campaign?.thumbnails[2] ? campaign.thumbnails[2] : DEFAULT_CAMPAIGN_IMAGE}
                    audience={campaign?.total_contacts?.toString()}
                    send={campaign?.total_sent?.toString()}
                    mail={campaign?.total_delivered?.toString()}
                    views={campaign?.total_open?.toString()}
                    clicks={campaign?.total_clicks?.toString()}
                    title={campaign?.name}
                    emailsCount={campaign?.emails_count}
                    published={campaign?.last_update}
                    onCardClick={() => {
                      if (campaign?.campaign_id?.toString() !== storedCampaignId) {
                        resetCampaignFilters();
                      }
                      campaignClick(campaign?.campaign_id?.toString());
                    }}
                    automation={campaign?.workflow === 1}
                  />
                </Col>
              ))}
              <Col className="gutter-row" xs={18} sm={12} md={8} lg={8} xl={8} xxl={8}>
                <div style={{ height: 285 }} className="add-btn-dropdown d-flex align-content-center justify-content-center flex-wrap">
                  <CreateCampaign hideDynamic={true}>
                    <AddCampaign className="add-campaign-plus-style" />
                  </CreateCampaign>
                </div>
              </Col>
            </>
          </Row>
        )}
      </InfiniteLoader>
    </Wrapper>
  );
};

export default MyCampaigns;

const DialogWrapper = styled.div`
  z-index: 17;
  top: 50%;
  left: 50%;
  position: fixed;
`;

const Wrapper = styled.div`
  overflow-x: hidden !important;
  .add-campaign-plus-style-list {
    height: 130px;
    margin-top: -30px;
  }
  .show-more-less-style {
    height: 325px;
  }
  h3 {
    font-size: 20px;
    color: #67696a;
    font-weight: 500;
    font-family: 'robotomedium';
    margin-bottom: 26px;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      font-size: 16px;
    }
  }
  .list-add {
    width: 100px;
  }
  .full-width {
    width: 100%;
  }
`;
